import type { Chapter, ChapterContext, MissionClock } from '../chapters/types';
import type { CinematicComposer } from '../render/composer';
import type { AudioEngine } from '../audio/engine';
import type { Hud } from '../ui/hud';
import type { MapOverlay } from '../ui/map';
import type { RunState } from './state';
import { LETTER, DEDICATION, TITLE, type Line } from '../data/letter';
import { showCard, clearCard, esc } from '../ui/forms';
import { formatDuration } from '../physics/schwarzschild';
import { postReply, postRun } from '../api/client';
import { TRACKS, CREDIT } from '../data/music';

type Mode = 'title' | 'chapter' | 'epilogue' | 'done';

const ORDINAL = ['First stop', 'Second stop', 'Third stop', 'Fourth stop', 'Last stop'];
const INTER_MS = 1700;

export interface DirectorDeps {
  chapters: Chapter[];
  composer: CinematicComposer;
  audio: AudioEngine;
  hud: Hud;
  map: MapOverlay;
  overlay: HTMLElement;
  canvas: HTMLCanvasElement;
  run: RunState;
  onRestart: () => void;
}

/** Runs the letter: the title, the chapters in order with a fade between them, the epilogue. Taps step the beats. */
export class Director {
  mode: Mode = 'title';
  index = -1;
  paused = false;
  readonly clock: MissionClock;
  private measurements = new Map<string, string>();
  private ctx: ChapterContext;
  private current: Chapter | null = null;
  private chapterFinished = false;
  private soundWasOn = false;
  private interTimer = 0;
  private epilogueDone = false;

  constructor(private d: DirectorDeps) {
    const self = this;
    this.clock = { earthSeconds: 0, shipSeconds: 0, rate: 1, advance(e, s) { this.earthSeconds += e; this.shipSeconds += s; if (e > 0) this.rate = s / e; } };
    this.ctx = {
      composer: d.composer, audio: d.audio, hud: d.hud, overlay: d.overlay, canvas: d.canvas, run: d.run, clock: this.clock,
      say: (lines: Line[]) => d.hud.say(lines),
      measure: (id, text) => { self.measurements.set(id, text); },
      finish: () => {
        self.chapterFinished = true;
        d.hud.setSkip(false); d.hud.setDemo(null);
        const next = d.chapters[self.index + 1];
        d.hud.setOnward(next ? `Next: ${next.title}` : 'The measure');
      }
    };
    d.map.setStops([
      { id: 'deeper', title: 'Deeper', r: 8.2, a: -1.9 },
      { id: 'wider', title: 'Wider', r: 13.5, a: -1.3 },
      { id: 'time', title: 'Time', r: 2.2, a: -2.6 },
      { id: 'light', title: 'Light', r: 4.8, a: -0.4 },
      { id: 'pit', title: 'All the way', r: 0, a: 0 }
    ]);
  }

  get chapter() { return this.current; }
  private setMode(m: Mode) { this.mode = m; document.body.dataset.mode = m; }
  private get wide() { return window.innerWidth > 760; }

  /** The title: the galaxy, the name, two lines, and the sound choice. */
  start() {
    this.setMode('title'); this.index = -1; this.current = null; this.epilogueDone = false;
    this.d.hud.hide();
    this.d.map.setMeasurements([]);
    this.d.map.show('', [], -1, -1, this.wide ? 1.15 : 0.95, this.wide ? 0.2 : 0, this.wide ? 0 : -0.14);
    const opening = LETTER.opening.map((l) => `<p class="${l.voice} big">${esc(l.text)}</p>`).join('');
    const root = this.d.overlay;
    root.innerHTML = `<div class="titleScreen"><h1>${esc(TITLE.name)}</h1><p class="tagline">${esc(TITLE.tagline)}</p>${opening}<p class="sub">About ten minutes. Headphones on if you can.</p><div class="row"><button type="button" class="pill gold" id="beginSound">Begin with sound</button><button type="button" class="pill ghost" id="beginSilent">Begin silent</button></div><p class="sub legal">${esc(CREDIT)}</p></div>`;
    root.querySelector('#beginSound')!.addEventListener('click', () => { this.d.audio.start(); this.d.hud.setSound(true); this.beginVoyage(); });
    root.querySelector('#beginSilent')!.addEventListener('click', () => this.beginVoyage());
  }

  private beginVoyage() {
    this.d.overlay.innerHTML = '';
    this.d.hud.show();
    this.d.hud.setProgress(this.d.chapters.map((c) => ({ id: c.id, title: c.title })), 0);
    this.d.audio.event('arrival');
    this.goTo(0);
  }

  /** Fade through black with the chapter's name, then the chapter. */
  private goTo(i: number) {
    window.clearTimeout(this.interTimer);
    if (this.current) { this.current.exit(); this.current = null; }
    const hud = this.d.hud;
    hud.setLever(null); hud.setMove(null); hud.setDemo(null); hud.setSkip(false); hud.setOnward(null); hud.setTapAlways(false);
    hud.clearSay(); hud.setReadouts([]); hud.setLabels([]); hud.setHint('');
    const next = this.d.chapters[i]; if (!next) { hud.hideInter(); this.epilogue(); return; }
    const meta = LETTER.chapters.find((c) => c.id === next.id);
    this.index = i; this.chapterFinished = false;
    this.d.map.hide();
    hud.setProgress(this.d.chapters.map((c) => ({ id: c.id, title: c.title })), i);
    hud.setChapter(next.title, i + 1, this.d.chapters.length);
    hud.setNav(i, this.d.chapters.length);
    hud.setNotes(meta?.notes ?? []);
    hud.setClocksVisible(!!next.wantsClocks);
    const bridge = (meta?.bridge ?? '').replace(/^[^:]+:s*/, '');
    hud.showInter(ORDINAL[i] ?? '', next.title, bridge.charAt(0).toUpperCase() + bridge.slice(1), meta?.place ?? '');
    this.enterChapter();
    this.interTimer = window.setTimeout(() => hud.hideInter(), INTER_MS);
  }

  private enterChapter() {
    const c = this.d.chapters[this.index]; this.current = c; this.setMode('chapter');
    this.d.composer.setScene(c.scene, c.camera);
    this.d.canvas.setAttribute('aria-label', c.description ?? c.title);
    this.d.hud.setDemo(c.demonstrate ? 'Show me' : null);
    this.d.hud.setSkip(true);
    this.d.audio.wake();
    const track = TRACKS.findIndex((t) => t.chapter === c.id);
    if (track >= 0 && this.d.audio.started) this.d.audio.cue(track);
    c.enter(this.ctx);
  }

  /** A tap on the right, Enter, Space or Right: the next beat; past the last one, the next chapter. */
  advance() {
    const hud = this.d.hud;
    if (hud.interUp) { window.clearTimeout(this.interTimer); hud.hideInter(); return; }
    if (hud.goOn()) return;
    if (this.mode === 'chapter' && this.chapterFinished) { this.goTo(this.index + 1); return; }
    if (this.mode === 'epilogue' && !this.epilogueDone) this.showFinal();
  }
  /** A tap on the left or Left: back a line. */
  retreat() { this.d.hud.goBack(); }
  /** Skip this chapter's exercise. */
  skip() { if (this.mode === 'chapter') this.goTo(this.index + 1); }
  /** A click on the map: during the epilogue it is a tap; during the title it does nothing. */
  onward() { if (this.mode === 'epilogue') this.advance(); }

  back() {
    if (this.mode === 'epilogue') { this.jump(this.d.chapters.length - 1); return; }
    if (this.mode !== 'title' && this.index > 0) this.jump(this.index - 1);
  }
  next() {
    if (this.mode === 'chapter') this.jump(this.index + 1);
  }
  /** A chapter was chosen: go there, from anywhere after the title, finished or not. */
  jump(i: number) {
    if (this.mode === 'title') return;
    if (this.mode === 'chapter' && i === this.index) return;
    clearCard(this.d.overlay);
    this.setPaused(false);
    this.goTo(i);
  }

  /** "Show me": the chapter does its discovery without the pointing game. */
  demonstrate() {
    if (this.d.hud.interUp) { window.clearTimeout(this.interTimer); this.d.hud.hideInter(); }
    this.current?.demonstrate?.();
  }

  togglePause() { this.setPaused(!this.paused); }
  private setPaused(p: boolean) {
    if (p === this.paused) return;
    this.paused = p;
    this.d.hud.setPaused(p);
    if (p) { this.soundWasOn = this.d.audio.started && !this.d.audio.isMuted; if (this.soundWasOn) this.d.audio.setMuted(true); }
    else if (this.soundWasOn) this.d.audio.setMuted(false);
  }

  /** The epilogue: the galaxy again, the closing lines as beats, then the last line stays. */
  private epilogue() {
    this.setMode('epilogue'); this.current = null; this.epilogueDone = false;
    const hud = this.d.hud;
    hud.setProgress(this.d.chapters.map((c) => ({ id: c.id, title: c.title })), this.d.chapters.length);
    hud.setChapter('The measure');
    hud.setNav(this.d.chapters.length, this.d.chapters.length);
    hud.setClocksVisible(false); hud.setNotes([]); hud.setReadouts([]);
    this.d.map.setMeasurements([]);
    this.d.map.show('', [], this.d.chapters.length - 1, this.d.chapters.length - 1, this.wide ? 1.05 : 0.9, 0, this.wide ? 0 : -0.12);
    this.d.audio.event('ending-hold');
    hud.setTapAlways(true);
    hud.say(LETTER.epilogue.slice(0, -1));
    void postRun(this.d.run);
  }

  /** The last line stays: quiet scene, the line, the signature. Everything else sits under it. */
  private showFinal() {
    this.epilogueDone = true;
    const hud = this.d.hud;
    hud.setTapAlways(false); hud.clearSay();
    const last = LETTER.epilogue[LETTER.epilogue.length - 1];
    const personal = LETTER.personal ? `<p class="personal">${esc(LETTER.personal)}</p>` : '';
    const sig = DEDICATION.from ? `<div class="sig">${esc(DEDICATION.from)}</div>` : '';
    const root = this.d.overlay;
    document.body.classList.add('card-open');
    root.innerHTML = `<div class="finalScreen">${personal}<p class="last">${esc(last.text)}</p>${sig}<div class="row"><button type="button" class="pill gold" id="fReply">Write back</button><div class="quiet"><button type="button" id="fNumbers">The numbers</button><button type="button" id="fAgain">Read it again</button></div></div></div>`;
    root.querySelector('#fReply')!.addEventListener('click', () => this.replyForm());
    root.querySelector('#fNumbers')!.addEventListener('click', () => this.showNumbers());
    root.querySelector('#fAgain')!.addEventListener('click', () => this.d.onRestart());
  }

  private showNumbers() {
    const clocks = `<div class="reveal"><div><div class="k">Earth's clock</div><div class="v">${esc(formatDuration(this.clock.earthSeconds))}</div></div><div><div class="k">Your clock</div><div class="v">${esc(formatDuration(this.clock.shipSeconds))}</div></div></div>`;
    const list = [...this.measurements.values()].map((m) => `<li>${esc(m)}</li>`).join('');
    showCard(this.d.overlay, `<h2>The numbers</h2>${clocks}<p>Five ways to measure it. ${this.measurements.size} kept.</p><ul class="kept">${list}</ul>`, [
      { label: 'Back to the letter', primary: true, onClick: () => this.showFinal() }
    ]);
  }

  private replyForm() {
    const card = showCard(this.d.overlay, `<h2>Write back</h2><p class="sub">Your words go straight to ${esc(DEDICATION.from || 'the person who wrote this')}, and nobody else.</p>
      <form id="reply"><label class="field">Your name (optional)<input type="text" id="rName" maxlength="80" autocomplete="off"></label>
      <label class="field">Your message<textarea id="rMsg" rows="5" maxlength="2000" required></textarea></label></form>`, [
      { label: 'Send', primary: true, onClick: async () => {
        const name = (card.querySelector<HTMLInputElement>('#rName')!.value || '').trim();
        const msg = (card.querySelector<HTMLTextAreaElement>('#rMsg')!.value || '').trim();
        if (!msg) { card.querySelector('#rMsg')!.setAttribute('aria-invalid', 'true'); return; }
        const ok = await postReply({ clientId: this.d.run.clientId, name, message: msg });
        showCard(this.d.overlay, ok ? `<h2>Sent</h2><p>It’s on its way, in the straightest line there is.</p>` : `<h2>Not sent</h2><p>The server couldn’t be reached. Copy your words and try again in a bit.</p><p class="sub">${esc(msg)}</p>`, [
          { label: 'Back to the letter', primary: true, onClick: () => this.showFinal() }
        ]);
      } },
      { label: 'Not now', onClick: () => this.showFinal() }
    ]);
    card.querySelector('form')!.addEventListener('submit', (e) => e.preventDefault());
  }

  // ---- input forwarding
  onDrag(dx: number, dy: number) { if (!this.paused) this.current?.onDrag(dx, dy); }
  onWheel(dy: number) { if (!this.paused) this.current?.onWheel(dy); }
  /** A tap on the picture: while the words step, the left third goes back and the rest goes on; otherwise the chapter has it. */
  onClick(x: number, y: number) {
    if (this.d.hud.interUp) { this.advance(); return; }
    if (this.d.hud.isReading || this.mode === 'epilogue') { if (x < this.d.canvas.clientWidth * 0.28) this.retreat(); else this.advance(); return; }
    if (!this.paused) this.current?.onClick(x, y);
  }
  /** Keys: while the words step, Right, Enter and Space go on and Left goes back; otherwise the chapter has them. */
  onKey(key: string): boolean {
    const hud = this.d.hud;
    const reading = hud.interUp || hud.isReading || this.mode === 'epilogue';
    if (reading) {
      if (key === 'Enter' || key === ' ' || key === 'ArrowRight') { this.advance(); return true; }
      if (key === 'ArrowLeft') { this.retreat(); return true; }
      return false;
    }
    if (this.mode === 'chapter' && this.current?.onKey && !this.paused) return !!this.current.onKey(key);
    return false;
  }

  /** Per frame. */
  update(dt: number, t: number, width: number, height: number) {
    this.d.map.update(dt);
    if (this.mode === 'chapter' && this.current) {
      if (!this.paused) this.current.update(dt, t, width, height);
      this.d.hud.setClocks(this.clock.earthSeconds, this.clock.shipSeconds, this.clock.rate);
      if (this.current.labels) this.d.hud.setLabels(this.current.labels(this.d.canvas.clientWidth, this.d.canvas.clientHeight));
    } else if (this.mode === 'title' || this.mode === 'epilogue') {
      this.d.audio.setScene({ rate: 1, dist: 60, phase: this.mode, orbitPhase: 0 });
    }
  }

  afterRender() { this.current?.afterRender?.(); }

  /** Whether the active chapter's scene should be rendered (the map covers it otherwise). */
  get renderScene() { return this.mode === 'chapter' && this.current !== null; }
}
